import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { IUser, loginInputSchema } from "muminsmart-utils";
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormControl,
  Form,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Mail, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";
import { routes } from "@/lib/constants";
import { getHomeRouteForUser } from "@/lib/routing";
import { philosopher } from "@/lib/fonts";
import { toast } from "sonner";
import AuthFormSkeleton from "../../common/skeletons/AuthFormSkeleton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import PhoneInputField from "@/components/common/PhoneInputField";
import { normalizePhoneNumber } from "../signup/ContactOtpVerification";

const getSafeCallbackUrl = (callbackUrl: string | null) => {
  if (!callbackUrl) {
    return null;
  }

  if (!callbackUrl.startsWith("/") || callbackUrl.startsWith("//")) {
    return null;
  }

  return callbackUrl;
};

const getPostLoginRedirect = (
  user: IUser,
  callbackUrl: string | null,
) => {
  const safeCallbackUrl = getSafeCallbackUrl(callbackUrl);
  if (safeCallbackUrl) {
    return safeCallbackUrl;
  }

  return getHomeRouteForUser(user);
};

const Login = () => {
  const { data: session, status, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl") ?? null;
  const form = useForm<z.input<typeof loginInputSchema>>({
    resolver: zodResolver(loginInputSchema),
    defaultValues: {
      email: "",
      mobile: "",
      password: "",
    },
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState<"email" | "mobile">("email");

  useEffect(() => {
    if (session && session.user.type) {
      const redirectUrl = getPostLoginRedirect(session.user, callbackUrl);

      if (redirectUrl) {
        navigate(redirectUrl);
        return;
      }

      toast.error("Unknown user type. Please contact support.");
    }
  }, [session, callbackUrl, navigate]);

  const onSubmit = async (values: z.input<typeof loginInputSchema>) => {
    const identifierValue =
      loginMethod === "email"
        ? values.email?.trim() || ""
        : normalizePhoneNumber(values.mobile || "");

    const payload = {
      password: values.password,
      [loginMethod]: identifierValue,
    };

    login(payload)
      .then((res) => {
        if (!res.ok) {
          toast.error(res.error ?? "Login failed");
          return;
        }

        const safeCallbackUrl = getSafeCallbackUrl(callbackUrl);
        if (safeCallbackUrl) {
          navigate(safeCallbackUrl);
        }
      })
      .catch((error) => toast.error(error.message));
  };

  if (status === "loading") {
    return <AuthFormSkeleton />;
  }

  return (
    <>
      <h1
        className={`text-center text-secondary text-3xl font-semibold ${philosopher.className} mb-3`}
      >
        <span>Sign </span>
        <span className="text-primary">In</span>
      </h1>
      <p className="text-[#6f6f6f] text-center text-sm mt-2 mb-10">
        Welcome back to your Bohra marketplace!
      </p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5 px-4">
          <FormItem>
            <FormLabel className="font-medium">Login With</FormLabel>
            <RadioGroup
              value={loginMethod}
              onValueChange={(value) => {
                const method = value as "email" | "mobile";
                setLoginMethod(method);
                if (method === "email") {
                  form.setValue("mobile", "", { shouldValidate: true });
                  form.clearErrors("mobile");
                } else {
                  form.setValue("email", "", { shouldValidate: true });
                  form.clearErrors("email");
                }
              }}
              className="grid grid-cols-2 gap-3"
            >
              <div>
                <RadioGroupItem
                  value="email"
                  id="login-with-email"
                  className="sr-only"
                />
                <label
                  htmlFor="login-with-email"
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-medium transition-all ${
                    loginMethod === "email"
                      ? "border-primary bg-primary/10 text-primary shadow-sm"
                      : "border-muted bg-white text-foreground hover:border-primary/40 hover:bg-primary/5"
                  }`}
                >
                  <Mail size={17} />
                  <span>Email</span>
                  {loginMethod === "email" && (
                    <span className="ml-auto h-2 w-2 rounded-full bg-primary" />
                  )}
                </label>
              </div>
              <div>
                <RadioGroupItem
                  value="mobile"
                  id="login-with-mobile"
                  className="sr-only"
                />
                <label
                  htmlFor="login-with-mobile"
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-medium transition-all ${
                    loginMethod === "mobile"
                      ? "border-primary bg-primary/10 text-primary shadow-sm"
                      : "border-muted bg-white text-foreground hover:border-primary/40 hover:bg-primary/5"
                  }`}
                >
                  <Smartphone size={17} />
                  <span>Mobile</span>
                  {loginMethod === "mobile" && (
                    <span className="ml-auto h-2 w-2 rounded-full bg-primary" />
                  )}
                </label>
              </div>
            </RadioGroup>
          </FormItem>

          {loginMethod === "email" ? (
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium">Email Id</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="example@gmail.com"
                      className="rounded-full p-5 bg-white"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : (
            <FormField
              control={form.control}
              name="mobile"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium">Mobile Number</FormLabel>
                  <FormControl>
                    <PhoneInputField
                      country={"in"}
                      value={field.value || ""}
                      onChange={(value) => field.onChange(value)}
                      inputClass={`!w-full !rounded-full !h-12 !pl-14 !pr-4 !text-base !bg-white ${
                        form.formState.errors.mobile
                          ? "!border-destructive"
                          : ""
                      }`}
                      buttonClass="!border-none !bg-transparent [&_.selected-flag]:!bg-transparent !pl-3"
                      containerClass="!w-full"
                      dropdownClass="!rounded-md"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-medium">Password</FormLabel>
                <FormControl>
                  <InputGroup className="rounded-full bg-white h-auto">
                    <InputGroupInput
                      type={showPassword ? "text" : "password"}
                      placeholder="********"
                      className="px-5 py-5"
                      {...field}
                      aria-invalid={
                        form.formState.errors.password ? "true" : "false"
                      }
                    />
                    <InputGroupAddon align="inline-end">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-muted"
                      >
                        {showPassword ? (
                          <EyeOff size={30} />
                        ) : (
                          <Eye size={30} />
                        )}
                      </button>
                    </InputGroupAddon>
                  </InputGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <p className="text-right">
            <Link
              to={routes.auth.forgotPassword}
              className="text-sm text-black underline"
            >
              Forgot Password?
            </Link>
          </p>

          <Button
            type="submit"
            className="w-full rounded-full bg-primary py-6 text-lg"
            disabled={form.formState.isSubmitting}
            aria-busy={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? "Signing In..." : "Sign In"}
          </Button>

          <p className="text-sm text-black text-center font-semibold">
            Don&apos;t have an account?{" "}
            <Link to={routes.auth.signup} className="text-primary underline">
              Sign Up
            </Link>
          </p>
        </form>
      </Form>
    </>
  );
};

export default Login;
